import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { BsX, BsHeartFill, BsDiamondFill, BsSuitClubFill, BsSuitSpadeFill } from 'react-icons/bs';
import './CardPickerDialog.css'; // We'll create this CSS file next

interface CardPickerDialogProps {
  cardPosition: 'card1' | 'card2' | 'flop1' | 'flop2' | 'flop3' | 'turn' | 'river';
  currentCard: string;
  onCardSelect: (card: string) => void;
  onCancel: () => void;
  getUsedCards: () => string[];
  show: boolean; // Added to control visibility from parent
}

const CARD_RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
const DISPLAY_RANKS = CARD_RANKS;
const CARD_SUITS = [
  { symbol: 'h', label: 'Hearts', icon: <BsHeartFill style={{ color: '#ff4560' }} /> },
  { symbol: 'd', label: 'Diamonds', icon: <BsDiamondFill style={{ color: '#ff4560' }} /> },
  { symbol: 'c', label: 'Clubs', icon: <BsSuitClubFill style={{ color: 'white' }} /> },
  { symbol: 's', label: 'Spades', icon: <BsSuitSpadeFill style={{ color: 'white' }} /> }
];

const CardPickerDialog: React.FC<CardPickerDialogProps> = ({
  cardPosition,
  currentCard,
  onCardSelect,
  onCancel,
  getUsedCards,
  show
}) => {
  const [selectedRank, setSelectedRank] = useState<string | null>(null);

  const isCardUsed = (rank: string, suitSymbol: string): boolean => {
    const card = rank + suitSymbol;
    const usedCards = getUsedCards();
    return usedCards.includes(card);
  };

  const handleRankSelection = (rank: string) => {
    setSelectedRank(rank);
  };

  const handleSuitSelection = (suitSymbol: string) => {
    if (!selectedRank) return;
    const card = selectedRank + suitSymbol;
    if (!isCardUsed(selectedRank, suitSymbol)) {
        onCardSelect(card);
    }
    setSelectedRank(null); 
  };

  if (!show) {
    return null;
  }

  return (
    <Dialog.Root open={show} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="card-dialog-overlay" />
        <Dialog.Content className="card-dialog-content">
          <Dialog.Title className="card-dialog-title">
            Select {cardPosition === 'card1' ? 'First' : cardPosition === 'card2' ? 'Second' : cardPosition.toUpperCase()} Card
          </Dialog.Title>

          <div className="card-dialog-body">
            {!selectedRank ? (
              <>
                <div className="card-dialog-label">Select Rank</div>
                <div className="rank-picker-horizontal">
                  {DISPLAY_RANKS.map(rank => (
                    <button
                      key={rank}
                      className="rank-button"
                      onClick={() => handleRankSelection(rank)}
                    >
                      {rank}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="card-dialog-label">Select Suit for {selectedRank}</div>
                <div className="suit-picker">
                  {CARD_SUITS.map(suit => {
                    const cardIsActuallyUsed = isCardUsed(selectedRank, suit.symbol);
                    return (
                      <button
                        key={suit.symbol}
                        className={`suit-button ${cardIsActuallyUsed ? 'used' : ''}`}
                        onClick={() => handleSuitSelection(suit.symbol)}
                        aria-label={suit.label}
                        disabled={cardIsActuallyUsed}
                      >
                        {suit.icon}
                      </button>
                    );
                  })}
                </div>
                <button 
                  className="back-button"
                  onClick={() => setSelectedRank(null)}
                >
                  ← Back to Ranks
                </button>
              </>
            )}
          </div>

          <Dialog.Close asChild>
            <button className="card-dialog-close" aria-label="Close" onClick={onCancel}>
              <BsX />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default CardPickerDialog; 